import type { NodeData } from "@/workflow_api/types/workflow";
import {
  Badge,
  GroupTextField,
  GroupTextFieldRow,
  Icon,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@elaniintc/goes-design-system";
import type { Node } from "@xyflow/react";
import { useEffect, useMemo } from "react";
import { TimerBlock } from "../timer/timer-block";
import type { TimerBlockValues, TimeValuesConfig } from "../timer/types";
import { GroupsProvider, useGroups } from "../assign-group/context";
import { GroupSelector } from "../assign-group/components/group-selector";
import type { GroupType } from "@/types/entities/approval-groups";
import { useWorkflowStore } from "@/workflow_api/store/workflowStore";
import { useUIStore } from "@/workflow_api/store/uiStore";
import SwitchField from "../form/components/SwitchField";

const GroupAssignmentSection: React.FC<{
  onGroupsChange: (groupIds: string[]) => void;
  assignedGroups: string[];
  setSelectedGroupsList: (values: GroupType[]) => void;
  selectedGroupsList: GroupType[];
  requiredLeadApprove: boolean;
  setRequiredLeadApprove: (values: boolean) => void;
}> = ({
  onGroupsChange,
  assignedGroups,
  setSelectedGroupsList,
  selectedGroupsList,
  requiredLeadApprove,
  setRequiredLeadApprove,
}) => {
  const { groups, selectedGroup, loadMore, hasMore, isLoadingMore } =
    useGroups();

  const handleGroupChange = (group: GroupType) => {
    // Toggle selection
    const isSelected = selectedGroupsList.some((g) => g.id === group.id);
    let newSelection: GroupType[];

    if (isSelected) {
      // Remove group
      newSelection = selectedGroupsList.filter((g) => g.id !== group.id);
    } else {
      // Add group
      newSelection = [...selectedGroupsList, group];
    }

    setSelectedGroupsList(newSelection);
    onGroupsChange(newSelection.map((g) => g.id));
  };

  useEffect(() => {
    if (
      assignedGroups.length > 0 &&
      groups.length > 0 &&
      selectedGroupsList.length === 0
    ) {
      const foundGroups = groups.filter((group) =>
        assignedGroups.includes(group.id),
      );
      if (foundGroups.length > 0) {
        setSelectedGroupsList(foundGroups);
      }
    }
  }, [
    assignedGroups,
    groups,
    selectedGroupsList.length,
    setSelectedGroupsList,
  ]);

  return (
    <div className="px-4 border-b border-neutral-quiet py-4 flex flex-col gap-2">
      <label className="font-semibold text-body-loud leading-l">
        Asignar grupos <span className="text-danger">*</span>
      </label>

      <GroupSelector
        groups={groups}
        selectedGroup={selectedGroup}
        onGroupChange={handleGroupChange}
        placeholder="Seleccionar grupos"
        onLoadMore={loadMore}
        hasMore={hasMore}
        isLoadingMore={isLoadingMore}
        selectedGroupsList={selectedGroupsList}
      />
      <div className="flex flex-col gap-0.5">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-xs leading-xl text-body-quiet">
              Asegúrate de que estos grupos hayan sido seleccionados previamente
              en nodos de "Asignar a Grupo".
            </p>
          </div>
        </div>
      </div>

      <SwitchField
        id="date-range-field-required"
        label="Aprobación requerida por el lider"
        required
        checked={requiredLeadApprove}
        onChange={setRequiredLeadApprove}
        isTrue="Sí"
        isFalse="No"
      />
    </div>
  );
};

interface Props {
  nodes: Node[];
  selectedForm?: string;
  setSelectedForm?: (form: string) => void;
  limit: string;
  setLimit: (value: string) => void;
  memoInitialValues: {
    externalData?: TimeValuesConfig;
    internalData?: TimeValuesConfig;
  };
  selectedGroups: string[];
  setSelectedGroups: (groupIds: string[]) => void;
  setInternalTimer: (values: TimerBlockValues) => void;
  setExternalTimer: (values: TimerBlockValues) => void;
  selectedGroupsList: GroupType[];
  setSelectedGroupsList: (values: GroupType[]) => void;
  requiredLeadApprove: boolean;
  setRequiredLeadApprove: (values: boolean) => void;
}

const CorrectionContent = ({
  limit,
  nodes,
  selectedForm,
  setLimit,
  setSelectedForm,
  memoInitialValues,
  selectedGroups,
  setSelectedGroups,
  setInternalTimer,
  setExternalTimer,
  selectedGroupsList,
  setSelectedGroupsList,
  requiredLeadApprove,
  setRequiredLeadApprove,
}: Props) => {
  const renderableNodes = useMemo(() => {
    return nodes.filter((variable) => {
      const inputId = (variable.data as unknown as NodeData).inputs?.find(
        (input) => input.type === "FORM",
      )?.value;
      return inputId;
    });
  }, [nodes]);

  const selectedVariable =
    renderableNodes.find((n) => {
      const inputId = (n.data as unknown as NodeData).inputs?.find(
        (input) => input.type === "FORM",
      )?.value;
      return inputId?.toString() === selectedForm;
    }) || null;
  const selectedFormId = selectedVariable
    ? (selectedVariable.data as unknown as NodeData).inputs?.find(
        (input) => input.type === "FORM",
      )?.value
    : null;

  const { selectedNodeId } = useUIStore();
  const { getNodeById } = useWorkflowStore();
  const selectedNode = getNodeById(selectedNodeId || "");
  const nodeData = selectedNode?.data as unknown as NodeData;

  return (
    <div className="w-full py-6 flex flex-col gap-4">
      <GroupTextField
        label="Formulario y límite de revisiones"
        helperText="Si no deseas activar un límite de revisiones, puedes dejar vacío ese dato."
        required
        className="w-full pb-6 px-4 border-b border-neutral-quiet"
      >
        <GroupTextFieldRow>
          <Select
            onValueChange={(val) => {
              const variable =
                nodes.find((v) => {
                  const inputId = (v.data as unknown as NodeData).inputs?.find(
                    (input) => input.type === "FORM",
                  )?.value;
                  return inputId?.toString() === val;
                }) || null;
              const inputId = (
                variable?.data as unknown as NodeData
              ).inputs?.find((input) => input.type === "FORM")?.value;
              setSelectedForm?.(inputId?.toString() || "");
            }}
            value={selectedFormId?.toString() || ""}
          >
            <SelectTrigger className="min-w-0 w-full flex" group="left">
              <div className="w-[90%] flex gap-3 items-center">
                {selectedVariable && (
                  <Badge
                    shape="pill"
                    variant="action"
                    className="size-6 rounded-full flex items-center justify-center"
                  >
                    <Icon type="far" name="bars" className="text-xs" />
                  </Badge>
                )}
                <p className="truncate text-body">
                  {`${selectedVariable?.data?.displayName || "Seleccione un formulario"}`}
                </p>
              </div>
            </SelectTrigger>
            <SelectContent className="max-h-110 overflow-y-auto border border-neutral py-2 max-w-(--radix-select-trigger-width)">
              {renderableNodes.length === 0 && (
                <p className="text-center text-sm text-body py-4">
                  No hay formularios disponibles.
                  <br /> Los formularios deben estar configurados para
                  configurar la Revisión
                </p>
              )}
              {renderableNodes.map((variable) => {
                const inputId = (
                  variable.data as unknown as NodeData
                ).inputs?.find((input) => input.type === "FORM")?.value;
                if (!inputId) return null;
                return (
                  <SelectItem
                    key={`${variable.id}-${variable.data.displayName}`}
                    value={inputId.toString() || ""}
                    className="*:block"
                  >
                    <div className="w-full flex items-center gap-3">
                      <Badge
                        shape="pill"
                        variant="action"
                        className="size-6 rounded-full flex items-center justify-center"
                      >
                        <Icon type="far" name="bars" className="text-xs" />
                      </Badge>
                      <p className="flex-1 text-body font-semibold truncate">
                        {`${variable.data.displayName}`}
                      </p>
                    </div>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
          <Input
            placeholder="Límite..."
            className="w-25"
            value={limit}
            onChange={(e) => {
              if (e.target.value === "") {
                setLimit("");
                return;
              }
              if (e.target.value.length > 3) return;
              const value = parseInt(e.target.value, 10);
              if (isNaN(value) || value <= 0) {
                setLimit("1");
              } else {
                setLimit(value.toString());
              }
            }}
          />
        </GroupTextFieldRow>
      </GroupTextField>

      <GroupsProvider inputs={nodeData?.inputs || []}>
        <GroupAssignmentSection
          onGroupsChange={(ids) => {
            setSelectedGroups(ids);
          }}
          assignedGroups={selectedGroups}
          selectedGroupsList={selectedGroupsList}
          setSelectedGroupsList={setSelectedGroupsList}
          setRequiredLeadApprove={setRequiredLeadApprove}
          requiredLeadApprove={requiredLeadApprove}
        />
      </GroupsProvider>

      {/* External */}
      <TimerBlock
        title="Temporizador (tramitador)"
        showLocalPreventions
        initialValue={memoInitialValues.externalData}
        onChange={setExternalTimer}
      />
      {/* Internal */}
      <TimerBlock
        title="Temporizador (analista)"
        initialValue={memoInitialValues.internalData}
        onChange={setInternalTimer}
      />
    </div>
  );
};

export default CorrectionContent;
